import { StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/*
 * Meta / UTM cart-attribute capture (tasks.db #1755, 2026-10-08).
 *
 * WHY: pagewright #560 (prod) reads the order's note_attributes for fbc / fbp / fbclid / utm_*
 * (packages/adapters/src/conversions.ts), but hx-analytics showed hasFbc 0/81, hasFbp 0/81,
 * hasUtmSource 0/81: the theme only ever wrote ga_client_id (assets/ga-client-id-capture.js). So
 * Meta CAPI events carried no fbc/fbp and orders carried no UTMs (EMQ 3.3). This module writes them
 * onto the CART so they ride to the order as note_attributes. Keys are exactly the ones pagewright
 * reads: fbc, fbp, fbclid, utm_source, utm_medium, utm_campaign, utm_content, utm_term.
 *
 * Flag-gated: snippets/scripts.liquid only emits this file when the theme setting
 * pw_cart_attribution_capture is 'on' (default off), so OFF = byte-identical to before.
 *
 * Rules (all pinned by tests/test_cart_attribution_capture_1755_100800.py):
 *  - CONSENT: nothing is read, stored or posted unless Shopify's Customer Privacy API says
 *    marketingAllowed() === true (the same signal Shopify's own Meta pixel honours). API absent,
 *    missing the function, or throwing = treated as NOT allowed (nothing captured, but nothing erased either);
 *    saleOfDataAllowed()===false (GPC) also blocks. An explicit decline clears the stored keys.
 *  - FAIL OPEN for the cart: every path is try/catch; the listener never blocks or delays
 *    add-to-cart/checkout; it only runs after event.promise settles; it only POSTs attributes.
 *  - NO PII: email-looking values are dropped, values are length-capped and control-char free,
 *    fbclid must match the click-id charset. Never writes a cookie. Never sends `note`.
 *  - First touch: UTMs / fbclid from the landing URL are kept in sessionStorage (sticky once a
 *    touch with signal is stored). fbc = the _fbc cookie if valid, else fb.1.<first-seen ms>.<fbclid>.
 *  - No empty carts: nothing is POSTed when there is nothing to send.
 *  - Idempotent per cart: signature = cart cookie + payload; a different cart (after checkout) gets
 *    the attributes again; a failed POST is not marked and retries on the next cart update.
 *  - WITHDRAWAL SCRUB: if consent is explicitly denied (incl. GPC) and a cart cookie exists, POST all 8
 *    keys as '' to /cart/update.js (empty removes them) so they never become order note_attributes
 *    (pagewright forwards fbc/fbp to Meta CAPI). Fast path = per-tab posted marker; with no marker
 *    (new session / other tab / storage blocked) GET /cart.js once per page load and scrub if any of the
 *    8 keys is present. Marker cleared only on success; failures retry on the next consent/cart event.
 * Separate file on purpose: stays out of pw-page-cart.js / layout/theme.liquid (pricing-ladder PRs).
 */

var TOUCH_KEY = 'pw_cart_attr_touch';
var POSTED_KEY = 'pw_cart_attr_posted';
var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
var MAX_UTM = 100;
var MAX_FBCLID = 255;
var ALL_KEYS = ['fbc', 'fbp', 'fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
var scrubbing = false;
/* /cart.js GET happens at most once per page load */
var cartChecked = false;
/* server-side attributes confirmed present: keep retrying the scrub POST */
var pendingScrub = false;
/* a capture POST succeeded on THIS page load (survives blocked storage): a later denial must scrub */
var postedThisLoad = false;

/* Trim, strip control chars, cap length, drop anything that looks like an email. '' = unusable. */
function sanitizeValue(v, max) {
  if (typeof v !== 'string') return '';
  var s = v.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  if (s.length > max) s = max === MAX_UTM ? s.slice(0, max) : '';
  if (!s || /\S+@\S+/.test(s)) return '';
  return s;
}

function readCookie(cookieString, name) {
  var parts = String(cookieString || '').split(';');
  for (var i = 0; i < parts.length; i++) {
    var p = parts[i].trim();
    if (p.indexOf(name + '=') === 0) return p.slice(name.length + 1);
  }
  return '';
}

/* Meta cookie shapes: fb.<subdomainIndex>.<creationMs>.<id> (_fbc id is the fbclid, _fbp id is digits). */
function validFbc(v) {
  return /^fb\.[0-2]\.\d{10,16}\.[A-Za-z0-9_-]{1,255}(\.[A-Za-z0-9_-]{1,255})*$/.test(v || '');
}
function validFbp(v) {
  return /^fb\.[0-2]\.\d{10,16}\.\d{1,20}$/.test(v || '');
}

/* 'granted' | 'denied' | 'unknown'. Only an EXPLICIT answer from the API counts: an API that is not
 * loaded yet (or throws) is 'unknown' -> do nothing, but never erase what was already stored. */
function consentState(win) {
  try {
    var cp = win && win.Shopify && win.Shopify.customerPrivacy;
    if (!cp || typeof cp.marketingAllowed !== 'function') return 'unknown';
    if (cp.marketingAllowed() !== true) return 'denied';
    /* Meta is also a "sale/share": honour sale-of-data opt-out (GPC sets it in some US states). */
    if (typeof cp.saleOfDataAllowed === 'function' && cp.saleOfDataAllowed() === false) return 'denied';
    return 'granted';
  } catch (e) {
    return 'unknown';
  }
}

function readTouchFromUrl(search) {
  var out = {};
  try {
    var params = new URLSearchParams(search || '');
    for (var i = 0; i < UTM_KEYS.length; i++) {
      var val = sanitizeValue(params.get(UTM_KEYS[i]) || '', MAX_UTM);
      if (val) out[UTM_KEYS[i]] = val;
    }
    var raw = params.get('fbclid') || '';
    if (raw.length <= MAX_FBCLID && /^[A-Za-z0-9_-]+$/.test(raw)) out.fbclid = raw;
  } catch (e) {
    /* malformed query string: capture nothing */
  }
  return out;
}

function hasSignal(t) {
  if (!t) return false;
  for (var k in t) {
    if (k !== 'fbclid_ts' && t[k]) return true;
  }
  return false;
}

/* UTMs: first touch wins once a touch WITH SIGNAL is stored (an empty stored touch never blocks a
 * real one). fbclid: the LATEST click wins (Meta derives fbc from the most recent ad click). */
function mergeTouch(stored, fresh, now) {
  var out = hasSignal(stored) ? Object.assign({}, stored) : Object.assign({}, fresh);
  if (hasSignal(stored) && fresh.fbclid && fresh.fbclid !== stored.fbclid) {
    out.fbclid = fresh.fbclid;
    out.fbclid_ts = now;
  } else if (out.fbclid && typeof out.fbclid_ts !== 'number') {
    out.fbclid_ts = now;
  }
  return hasSignal(out) ? out : stored || {};
}

function buildAttributes(env) {
  var touch = mergeTouch(env.stored, readTouchFromUrl(env.search), env.now);
  var attrs = {};
  for (var i = 0; i < UTM_KEYS.length; i++) {
    var v = sanitizeValue(touch[UTM_KEYS[i]] || '', MAX_UTM);
    if (v) attrs[UTM_KEYS[i]] = v;
  }
  var fbclid = typeof touch.fbclid === 'string' && /^[A-Za-z0-9_-]{1,255}$/.test(touch.fbclid) ? touch.fbclid : '';
  if (fbclid) attrs.fbclid = fbclid;
  var fbcCookie = readCookie(env.cookie, '_fbc');
  if (validFbc(fbcCookie)) {
    attrs.fbc = fbcCookie;
    attrs.fbclid = fbcCookie.split('.')[3]; /* keep fbclid consistent with the fbc actually sent */
  } else if (fbclid) attrs.fbc = 'fb.1.' + (typeof touch.fbclid_ts === 'number' ? touch.fbclid_ts : env.now) + '.' + fbclid;
  var fbp = readCookie(env.cookie, '_fbp');
  if (validFbp(fbp)) attrs.fbp = fbp;
  return { attributes: attrs, touch: touch };
}

function clearStoredTouch() {
  try {
    sessionStorage.removeItem(TOUCH_KEY);
    sessionStorage.removeItem(POSTED_KEY);
  } catch (e) {
    /* nothing to clear / storage blocked */
  }
}

function postScrub() {
  var blank = {};
  for (var i = 0; i < ALL_KEYS.length; i++) blank[ALL_KEYS[i]] = '';
  scrubbing = true;
  fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ attributes: blank }) }))
    .then(function (response) {
      scrubbing = false;
      if (!response.ok) return;
      pendingScrub = false;
      postedThisLoad = false;
      cartChecked = true; /* cart is clean now: no need to GET it again this page load */
      try {
        sessionStorage.removeItem(POSTED_KEY);
      } catch (e) {
        /* storage blocked */
      }
      /* consent re-granted while the scrub was in flight: capture skipped on the old marker, re-post now */
      if (consentState(window) === 'granted') captureCartAttribution();
    })
    .catch(function () {
      scrubbing = false; /* retried on the next consent/cart event */
    });
}

function hasOurAttributes(cart) {
  var attrs = cart && cart.attributes;
  if (!attrs || typeof attrs !== 'object') return false;
  for (var i = 0; i < ALL_KEYS.length; i++) {
    var v = attrs[ALL_KEYS[i]];
    if (v !== undefined && v !== null && String(v) !== '') return true;
  }
  return false;
}

/* Withdrawn consent: remove from the cart what we already wrote. Cart attributes live server-side for
 * the cart cookie's lifetime, so the per-tab posted marker is only the FAST path: with no marker (new
 * session, second tab, storage blocked) read /cart.js once per page load and scrub if any of our keys
 * is present. Needs a cart cookie (never creates a cart). Never throws; at most one request in flight. */
function scrubCartAttribution() {
  try {
    if (scrubbing || !readCookie(document.cookie, 'cart')) return;
    var marker = null;
    try {
      marker = sessionStorage.getItem(POSTED_KEY);
    } catch (e) {
      marker = null;
    }
    if (marker || pendingScrub || postedThisLoad) {
      postScrub();
      return;
    }
    if (cartChecked) return;
    cartChecked = true;
    scrubbing = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('cart.js ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        scrubbing = false;
        if (hasOurAttributes(cart) && consentState(window) === 'denied') {
          pendingScrub = true;
          postScrub();
        }
      })
      .catch(function () {
        scrubbing = false; /* no retry this page load: no loops */
      });
  } catch (e) {
    scrubbing = false;
  }
}

/* Consent was collected/changed: allow one fresh /cart.js check for this new consent cycle. */
function onConsentChange() {
  cartChecked = false;
  rememberTouch();
}

/* Cart page only: never create a cart just to attach attributes. Localized paths (/en-ca/cart) ok. */
function cartPageEligible(pathname, cookie) {
  return /(^|\/)cart\/?$/.test(pathname || '') && !!readCookie(cookie, 'cart');
}

function readStoredTouch() {
  try {
    var raw = sessionStorage.getItem(TOUCH_KEY);
    var parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}
function writeStoredTouch(t) {
  try {
    sessionStorage.setItem(TOUCH_KEY, JSON.stringify(t));
  } catch (e) {
    /* storage blocked: capture just doesn't persist across pages */
  }
}

/* Page-load step: remember the landing touch (consent-gated, no network, no cart creation). */
function rememberTouch() {
  try {
    var state = consentState(window);
    if (state !== 'granted') {
      if (state === 'denied') {
        /* explicit decline/withdrawal: keep nothing, and scrub what is already on the cart */
        if (readCookie(document.cookie, 'cart')) {
          try {
            sessionStorage.removeItem(TOUCH_KEY);
          } catch (e) {
            /* storage blocked */
          }
          scrubCartAttribution(); /* removes the marker itself once the scrub succeeds */
        } else clearStoredTouch();
      }
      return;
    }
    var built = buildAttributes({ cookie: document.cookie, search: location.search, stored: readStoredTouch(), now: Date.now() });
    if (hasSignal(built.touch)) writeStoredTouch(built.touch);
  } catch (e) {
    /* never break the page */
  }
}

/* Cart step: POST the attributes onto the cart. Never throws; returns nothing the caller must await. */
function captureCartAttribution() {
  try {
    if (consentState(window) !== 'granted') {
      if (consentState(window) === 'denied') rememberTouch(); /* clears touch + scrubs the cart */
      return;
    }
    rememberTouch(); /* consent may have been granted after landing; the URL is still this page's */
    var built = buildAttributes({ cookie: document.cookie, search: location.search, stored: readStoredTouch(), now: Date.now() });
    var attrs = built.attributes;
    if (Object.keys(attrs).length === 0) return;
    var signature = readCookie(document.cookie, 'cart') + '|' + JSON.stringify(attrs);
    var posted = null;
    try {
      posted = sessionStorage.getItem(POSTED_KEY);
    } catch (e) {
      posted = null;
    }
    if (posted === signature) return;
    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ attributes: attrs }) }))
      .then(function (response) {
        if (!response.ok) return;
        postedThisLoad = true;
        try {
          sessionStorage.setItem(POSTED_KEY, signature);
        } catch (e) {
          /* worst case: re-POST the same values next time */
        }
        /* consent withdrawn while this POST was in flight: the denial path saw no marker, so scrub now */
        if (consentState(window) === 'denied') scrubCartAttribution();
      })
      .catch(function () {
        /* best-effort: retried on the next cart update */
      });
  } catch (e) {
    /* synchronous failure (fetch/config): the cart must work exactly as before */
  }
}

try {
  rememberTouch();
  /* The consent API may not be ready yet / consent may come later: re-run when Shopify says so. */
  document.addEventListener('visitorConsentCollected', onConsentChange);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', onConsentChange);
  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function' && !(window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.marketingAllowed === 'function')) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], onConsentChange);
    }
  } catch (e) {
    /* optional */
  }
  document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
    try {
      /* cartLinesUpdate fires BEFORE the cart mutation completes: wait for it (same idiom as
       * ga-client-id-capture.js). The short delay keeps our /cart/update.js from running at the
       * same instant as the GA capture's (Shopify cart writes are best sent one at a time). */
      var later = function () {
        setTimeout(captureCartAttribution, 400);
      };
      if (event && event.promise && typeof event.promise.then === 'function') {
        event.promise.then(later).catch(later);
      } else {
        later();
      }
    } catch (e) {
      /* never surface */
    }
  });
  /* Cart page: the cart may predate the visitor's consent/landing. Never creates a cart. */
  var onCartPage = function () {
    if (cartPageEligible(location.pathname, document.cookie)) captureCartAttribution();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onCartPage);
  else onCartPage();
} catch (e) {
  /* module init must never break the theme */
}
